<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { ref, onMounted, watch, nextTick, onUnmounted } from 'vue';
import TrialPopup from '@/Components/Homepage/TrialPopup.vue';
import LogoSoundOn from '@/Components/Homepage/LogoSoundOn.vue';

import AskPreview from '@/Components/Homepage/AskPreview.vue';

import HowItWorks from '@/Components/Homepage/HowItWorks.vue';

import DualEditor from '@/Components/Homepage/DualEditor.vue';
import ProjectAnalysisPreview from '@/Components/Homepage/ProjectAnalysisPreview.vue';

defineProps({
    canLogin: {
        type: Boolean,
    },
    canRegister: {
        type: Boolean,
    },
});

const isChromium = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;

const words = ['research', 'development', 'discovery'];
const currentWordIndex = ref(0);
const nextWordIndex = ref(0);
const wordRef = ref(null);
const underlineWidth = ref('0px');
const containerWidth = ref('0px');
const nextUnderlineWidth = ref('0px');
const isTransitioning = ref(false);
const TIME_INTERVAL = 3000;

const showOverlay = ref(true);
const isLogoClicked = ref(false);
const audioPlayer = ref(null);

let animationFrameId = null;

const showTrialPopup = ref(false);
const popupDismissed = ref(false);

const measureWidth = (text) => {
    const temp = document.createElement('span');
    temp.style.visibility = 'hidden';
    temp.style.position = 'absolute';
    temp.style.fontSize = window.getComputedStyle(wordRef.value || document.body).fontSize;
    temp.style.fontFamily = window.getComputedStyle(wordRef.value || document.body).fontFamily;
    temp.style.fontWeight = window.getComputedStyle(wordRef.value || document.body).fontWeight;
    temp.style.letterSpacing = window.getComputedStyle(wordRef.value || document.body).letterSpacing;
    temp.textContent = text;
    document.body.appendChild(temp);
    const width = temp.offsetWidth + 4; // added +4 eyeballing a good value, before w/o it was finishing before the last letter
    document.body.removeChild(temp);
    return `${width}px`;
};

const updateWord = () => {
    isTransitioning.value = true;
    nextWordIndex.value = (currentWordIndex.value + 1) % words.length;
    nextUnderlineWidth.value = measureWidth(words[nextWordIndex.value]);
    
    setTimeout(() => {
        underlineWidth.value = nextUnderlineWidth.value;
        currentWordIndex.value = nextWordIndex.value;
        isTransitioning.value = false;
        updateContainerWidth();
    }, TIME_INTERVAL);
};

const updateContainerWidth = () => {
    nextTick(() => {
        if (wordRef.value) {
            const width = `${wordRef.value.offsetWidth}px`;
            containerWidth.value = width;
        }
    });
};

const playChirpSound = () => {
    if (audioPlayer.value) {
        audioPlayer.value.currentTime = 0;
        audioPlayer.value.volume = 0.05;
        audioPlayer.value.play();
    }
};

const handleLogoClick = () => {
    if (!isLogoClicked.value) {
        isLogoClicked.value = true;
        playChirpSound();
        setTimeout(() => {
            isLogoClicked.value = false;
        }, 1000);
    }
};

const handleScroll = () => {
    if (window.scrollY > 100 && !showTrialPopup.value && !popupDismissed.value) {
        showTrialPopup.value = true;
    }
};

const handlePopupClose = () => {
    showTrialPopup.value = false;
    popupDismissed.value = true;
};

onMounted(() => {
    audioPlayer.value = new Audio('/assets/sound/cricket-chirp.mp3');

    // Initial underline width for the first word in the tagline
    if (wordRef.value) {
        underlineWidth.value = measureWidth(words[currentWordIndex.value]);
        updateContainerWidth();
    }

    // Update the word every TIME_INTERVAL seconds
    let lastTime = performance.now();
    const animate = (currentTime) => {
        if (currentTime - lastTime >= TIME_INTERVAL) {
            updateWord();
            lastTime = currentTime;
        }
        animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);

    // Add scroll handler
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    if(animationFrameId) {
        cancelAnimationFrame(animationFrameId);
    }

    window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
    <Head title="Jimmy the Cricket" />

    <div class="bg-gray-50 text-gray-600">
        <TrialPopup 
            v-model="showTrialPopup" 
            text="Jimmy the Cricket is free in trial phase!"
            @update:modelValue="handlePopupClose"
        >
            <template v-slot:icon>
                FREE
            </template>
        </TrialPopup>

        <div class="relative flex min-h-screen flex-col items-center justify-center ">
            <div class="relative w-full max-w-7xl px-6 mt-8">
                <!-- Navigation -->
                <nav v-if="canLogin" class="right-6 top-6 flex justify-end gap-4">
                    <Link
                        v-if="$page.props.auth.user"
                        :href="route('dashboard')"
                        class="rounded-md px-4 py-2 text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-100 focus:outline-none"
                    >
                        Dashboard
                    </Link>
                    <template v-else>
                        <Link
                            :href="route('login')"
                            class="rounded-md px-4 py-2 text-gray-600 ring-1 ring-gray-200 transition 
                                    hover:bg-gray-100 focus:outline-none"
                        >
                            Log in
                        </Link>

                        <Link
                            v-if="canRegister"
                            :href="route('register')"
                            class="rounded-md px-4 py-2 text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-100 focus:outline-none"
                        >
                            Register
                        </Link>
                    </template>
                </nav>

                <!-- Main Content -->
                <main class="text-center">
                    <div class="flex flex-row justify-center align-center 
                        gap-2 sm:gap-6 mt-6
                    ">
                        <div class="mt-4 sm:mt-8 mb-2 cursor-pointer relative" @click="handleLogoClick">
                            <div
                                class="w-6 sm:w-12 mx-auto opacity-90 transform rotate-6"
                                v-if="!isLogoClicked"
                            >
                                <svg viewBox="0 0 344 282" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M45.4065 41C45.4065 72.4612 45.4065 103.922 45.4065 135.384C45.4065 141.49 40.3907 174.806 49.3031 178.463C66.6037 185.56 97.9833 142.486 105.587 133.435C120.879 115.234 134.572 95.8188 149.532 77.368C150.2 76.5442 159.817 62.6755 161.221 69.8996C164.621 87.3807 162.864 107.366 163.278 124.993C163.933 152.884 164.252 180.763 164.252 208.661C164.252 218.308 162.988 229.238 164.469 238.859C165.515 245.661 176.641 233.167 177.89 231.932C211.495 198.705 227.842 153.185 251.059 112.87C251.326 112.407 290.123 44.3018 296.519 71.3067C303.303 99.9486 300.308 132.907 300.524 162.01C300.682 183.297 300.632 204.584 300.632 225.871" stroke="#46CE5E" stroke-width="80.057" stroke-linecap="round"/>
                                </svg>
                            </div>
                            <div 
                                class="w-6 sm:w-12 mx-auto"
                                v-else
                            >
                                <!-- Alternative SVG when clicked -->
                                <svg viewBox="0 0 344 282" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M45.4065 41C45.4065 72.4612 45.4065 103.922 45.4065 135.384C45.4065 141.49 40.3907 174.806 49.3031 178.463C66.6037 185.56 97.9833 142.486 105.587 133.435C120.879 115.234 134.572 95.8188 149.532 77.368C150.2 76.5442 159.817 62.6755 161.221 69.8996C164.621 87.3807 162.864 107.366 163.278 124.993C163.933 152.884 164.252 180.763 164.252 208.661C164.252 218.308 162.988 229.238 164.469 238.859C165.515 245.661 176.641 233.167 177.89 231.932C211.495 198.705 227.842 153.185 251.059 112.87C251.326 112.407 290.123 44.3018 296.519 71.3067C303.303 99.9486 300.308 132.907 300.524 162.01C300.682 183.297 300.632 204.584 300.632 225.871" stroke="#46CE5E" stroke-width="80.057" stroke-linecap="round"/>
                                </svg>
                            </div>
                        </div>
                        
                        <div class="flex flex-row justify-center items-center gap-1 text-2xl sm:text-2xl font-bold text-slate-900">
                            <div class="font-logo ml-3 mr-1">jimmy</div>
                            <div class="font-medium text-slate-700"> the cricket</div>
                        </div>
                    </div>
                    
                    <div class="py-12 sm:py-24 flex flex-col gap-8 sm:gap-1">
                        <!--
                        <div class="text-slate-600 leading-relaxed 
                            text-4xl sm:text-5xl
                            block flex-wrap justify-center items-center gap-2 sm:gap-0">
                            <div class="inline">a 
                                <span class="font-extrabold text-green-700">smart notepad</span> 
                                for all your
                            </div>
                            <div class="word-container relative inline-block mx-4">
                                <transition name="word" mode="out-in">
                                    <span 
                                        :key="currentWordIndex" 
                                        class="word 
                                            flex justify-center items-center
                                            sm:inline text-center sm:text-left font-extrabold text-green-700" 
                                        ref="wordRef"
                                    >
                                        {{ words[currentWordIndex] }}
                                    </span>
                                </transition>
                                <span class="word-underline absolute left-0 right-0 sm:left-auto sm:right-auto" :style="{ width: underlineWidth }"></span>
                            </div>
                        </div>
                        -->

                        <!--
                        <div class="text-slate-500 text-xl sm:text-2xl">
                            we take your messy notes, <span class="font-bold">improve</span> them and allow you to <span class="font-bold">ask questions</span> about them
                        </div>
                         -->
                        
                        <!--
                        <div class="text-slate-600 leading-relaxed 
                            text-4xl sm:text-6xl
                            block flex-wrap justify-center items-center gap-2 sm:gap-0
                            font-medium
                            ">
                            <div class="inline">
                                Scribble less,
                                Remember <span class="font-extrabold text-green-700">more</span>
                            </div>
                        </div>
                        
                        <div class="text-slate-500 text-xl sm:text-3xl">
                            Scribble less, Remember <span class="font-bold">more</span>.
                        </div>
                        -->

                        <div class="text-slate-600
                            text-4xl sm:text-5xl
                            block flex-wrap justify-center items-center gap-2
                            leading-relaxed sm:leading-[1.3]
                            "
                        >
                            <div class="inline">
                                <span class="font-extrabold text-green-700">AI</span> note-taking
                                for customer
                            </div>
                            <div class="word-container relative inline-block mx-4">
                                <transition name="word" mode="out-in">
                                    <span 
                                        :key="currentWordIndex" 
                                        class="word 
                                            inline-block
                                            text-center sm:text-left font-extrabold text-green-700" 
                                        ref="wordRef"
                                    >
                                        {{ words[currentWordIndex] }}
                                    </span>
                                </transition>
                                <span class="word-underline absolute left-0 right-0 sm:left-auto sm:right-auto" :style="{ width: underlineWidth }"></span>
                            </div>
                        </div>
                        
                        <div class="text-slate-500 text-xl sm:text-3xl">
                            Be present, Capture <span class="font-extrabold">everything</span>.
                        </div>

                        <div>
                            <Link 
                                v-if="canRegister" 
                                :href="route('register')" 
                                class="inline-flex items-center 
                                    sm:mt-12
                                    px-8 sm:px-12 py-6 rounded-lg text-2xl font-bold 
                                    text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 
                                    hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <span>Register for free</span>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </Link>
                        </div>
                    </div>

                    <!-- Ask box -->
                    <div class="flex flex-col sm:flex-row 
                        sm:px-8 py-12 sm:pt-24 relative gap-8
                    ">
                        <!--  First section -->
                        <div class="flex grow-[1] sm:min-w-80 flex-col sm:flex-1 justify-center items-center gap-6">
                            <!-- Ask with icon -->
                            <div class="flex gap-2 items-center justify-center">
                                <span class="font-jakarta font-bold 
                                    text-5xl 
                                    bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                                    Ask
                                </span>
                                <svg
                                    class="w-8 sm:w-12 aspect-square mr-2 inline-flex text-emerald-600 group-hover:rotate-12 transition-transform duration-300 flex-shrink-0"
                                    fill="none" 
                                    stroke="currentColor" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    viewBox="0 0 24 24"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <span class="text-xl sm:text-2xl text-gray-500 max-w-64">anything about your interviews.</span>
                            <span class="-mt-4 text-base sm:text-lg text-gray-400 max-w-64">(* even during the interview)</span>
                        </div>

                        <div class="flex grow-[3] sm:flex-row flex-col gap-12 items-center self-center max-sm:max-w-[300px]">
                            <AskPreview />

                            <div class="grow-2 flex flex-col justify-center items-center">
                                <div class="text-base font-logo transform -rotate-2 text-gray-700">Back to... the interview!</div>
                                <img src="/assets/images/homepage/bttf-car.svg" alt="Chrome" class="w-32 mt-3">
                            </div>
                        </div>
                    </div>

                    <!-- Recap box -->
                    <div class="flex flex-col sm:flex-row 
                        sm:px-8 pt-8 pb-12 relative gap-8
                    ">
                        <!--  First section -->
                        <div class="flex grow-[1] sm:min-w-80 flex-col sm:flex-1 justify-center items-center gap-3 sm:gap-6 sm:order-2">
                            <!-- Refine with icon -->
                            <div class="flex gap-4 items-center justify-center">
                                <span class="font-jakarta font-bold 
                                    text-5xl 
                                    bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent
                                ">
                                    Catch up
                                </span>
                                <svg
                                    class="w-8 sm:w-12 aspect-square mr-2 inline-flex text-emerald-600 group-hover:rotate-12 transition-transform duration-300 flex-shrink-0" viewBox="0 0 512 512" fill="currentColor">
                                    <path d="M493.7 .9L299.4 75.6l2.3-29.3c1-12.8-12.8-21.5-24-15.1L101.3 133.4C38.6 169.7 0 236.6 0 309C0 421.1 90.9 512 203 512c72.4 0 139.4-38.6 175.7-101.3L480.8 234.3c6.5-11.1-2.2-25-15.1-24l-29.3 2.3L511.1 18.3c.6-1.5 .9-3.2 .9-4.8C512 6 506 0 498.5 0c-1.7 0-3.3 .3-4.8 .9zM192 192a128 128 0 1 1 0 256 128 128 0 1 1 0-256zm0 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm16 96a16 16 0 1 0 0-32 16 16 0 1 0 0 32z"/>
                                </svg>
                            </div>
                            <span class="text-xl sm:text-2xl text-gray-500 max-w-64">on past interviews and what's up next</span>
                        </div>

                        <div class="flex grow-[3] flex-col gap-12 items-center self-center max-sm:max-w-[300px]">
                            <ProjectAnalysisPreview />
                            <div class="flex flex-col justify-center items-center gap-3">
                                <div class="text-lg font-logo transform -rotate-2 text-gray-700">Jumping from interview to interview?</div>
                                <div class="text-lg text-gray-700 mt-2 max-w-80">No worries, you can quickly get up to speed!</div>
                            </div>
                        </div>
                    </div>

                    <!-- Refine box -->
                    <div class="flex flex-col sm:flex-row 
                        sm:px-8 pt-8 pb-12 relative gap-8
                    ">
                        <!--  First section -->
                        <div class="flex grow-[1] sm:min-w-80 flex-col sm:flex-1 justify-center items-center gap-3 sm:gap-6">
                            <!-- Refine with icon -->
                            <div class="flex gap-2 items-center justify-center">
                                <span class="font-jakarta font-bold 
                                    text-5xl 
                                    bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent
                                ">
                                    Refine
                                </span>
                                <svg
                                    class="w-8 sm:w-12 aspect-square mr-2 inline-flex text-emerald-600 group-hover:rotate-12 transition-transform duration-300 flex-shrink-0"
                                    viewBox="0 0 40 40" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M12.075 16.9149L15.5165 3.83744C15.6473 3.34044 16.3527 3.34044 16.4835 3.83744L19.925 16.9149C19.9713 17.0908 20.1094 17.2277 20.2857 17.2724L30.0868 19.7553C30.5897 19.8827 30.5897 20.5973 30.0868 20.7247L20.2931 23.2057C20.1129 23.2514 19.9729 23.3933 19.9296 23.5741L16.4863 37.9674C16.3639 38.4789 15.6361 38.4789 15.5137 37.9674L12.0704 23.5741C12.0271 23.3933 11.8871 23.2514 11.7069 23.2057L1.91324 20.7247C1.4103 20.5973 1.41029 19.8827 1.91324 19.7553L11.7143 17.2724C11.8906 17.2277 12.0287 17.0908 12.075 16.9149Z" fill="currentColor"/>
                                    <path d="M30.075 7.31494L31.5165 1.83744C31.6473 1.34044 32.3527 1.34044 32.4835 1.83744L33.925 7.31494C33.9713 7.49084 34.1094 7.62772 34.2857 7.67239L38.0868 8.63531C38.5897 8.76273 38.5897 9.47727 38.0868 9.60469L34.2931 10.5657C34.1129 10.6114 33.9729 10.7533 33.9296 10.9341L32.4863 16.9674C32.3639 17.4789 31.6361 17.4789 31.5137 16.9674L30.0704 10.9341C30.0271 10.7533 29.8871 10.6114 29.7069 10.5657L25.9132 9.60469C25.4103 9.47727 25.4103 8.76273 25.9132 8.63531L29.7143 7.67239C29.8906 7.62772 30.0287 7.49084 30.075 7.31494Z" fill="currentColor"/>
                                </svg>
                            </div>
                            <span class="text-xl sm:text-2xl text-gray-500 max-w-64">from messy notes to organized insights.</span>
                        </div>

                        <div class="flex grow-[3] flex-col gap-12 items-center overflow-x-scroll">
                            <DualEditor />
                            <!--
                            <div class="flex flex-col justify-center items-center gap-3">
                                <div class="text-lg font-logo transform -rotate-2 text-gray-700">Forgot to take notes of a topic?</div>
                                <div class="text-lg text-gray-700 mt-2 max-w-80">No worries, add it (after the meeting) and let us take care of it!</div>
                            </div>
                            -->
                        </div>
                    </div>

                    <!-- Features Section -->
                    <div class="w-full px-6 sm:px-10 py-8 sm:py-14 rounded-2xl">
                        <div class="max-w-5xl mx-auto">
                            <!-- Section container - horizontal on desktop, vertical on mobile -->
                            <div class="flex flex-col md:flex-row md:space-x-8 space-y-12 md:space-y-0">
                                
                                <!-- No app needed section -->
                                <div class="flex-1 bg-slate-100 rounded-xl p-6 shadow-sm flex flex-col">
                                    <div class="flex flex-col items-center text-xl sm:text-2xl font-bold text-gray-800 mb-4 text-center gap-3">
                                        <span class="font-logo block">No app needed!</span> 
                                        <span class="text-lg font-medium block">Works with apps in your <span class="underline">browser tab</span></span>
                                    </div>
                                    <div class="flex flex-wrap justify-center items-center flex-grow gap-x-2 gap-y-0 [&_.app-logo]:w-6 [&_.app-logo]:sm:w-10 my-auto">
                                        <!-- Google Meet -->
                                        <div class="flex flex-col items-center w-16 -rotate-6">
                                            <img src="/assets/images/product-logos/google-meet.svg" alt="Google Meet" class="app-logo aspect-square mb-2">
                                            <span class="text-gray-700 text-center text-xs">Meet</span>
                                        </div>
                                        <!-- Microsoft Teams -->
                                        <div class="flex flex-col items-center w-16 rotate-6">
                                            <img src="/assets/images/product-logos/msft-teams.svg" alt="Microsoft Teams" class="app-logo aspect-square mb-2">
                                            <span class="text-gray-700 text-center text-xs">Teams</span>
                                        </div>
                                        <!-- Zoom -->
                                        <div class="flex flex-col items-center w-16 -rotate-6">
                                            <img src="/assets/images/product-logos/zoom.svg" alt="Zoom" class="app-logo aspect-square mb-2">
                                            <span class="text-gray-700 text-center text-xs">Zoom</span>
                                        </div>
                                        <!-- YouTube -->
                                        <div class="flex flex-col items-center w-16 rotate-6">
                                            <img src="/assets/images/product-logos/youtube.svg" alt="YouTube" class="app-logo aspect-square mb-2">
                                            <span class="text-gray-700 text-center text-xs">YouTube</span>
                                        </div>
                                        <!-- Browser -->
                                        <div class="flex flex-col items-center w-16">
                                            <div class="app-logo aspect-square mb-2 text-center font-extrabold 
                                                text-sm sm:text-lg text-gray-500
                                                flex items-center justify-center transform -rotate-12"
                                            >
                                                More!
                                            </div>
                                            <span class="text-gray-700 text-center text-xs">Any Site</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- No awkward bot section -->
                                <div class="flex-1 bg-slate-100 rounded-xl p-6 shadow-sm flex flex-col">
                                    <div class="flex flex-col items-center text-xl sm:text-2xl font-bold text-gray-800 mb-4 text-center gap-3">
                                        <span class="font-logo block">No awkward bot</span> 
                                        <span class="text-lg block font-medium ">joining your call!</span>
                                    </div>
                                    <div class="flex flex-col items-center justify-center flex-grow">
                                        <img src="/assets/images/homepage/awkward-robot.svg" alt="Awkward Robot" class="w-24 sm:w-36">
                                        <!--<p class="text-gray-600 text-center">
                                            Record and transcribe your in-person<br>
                                            meetings and discussions.
                                        </p>-->
                                    </div>
                                </div>
                                
                                <!-- For offline meetings section -->
                                <div class="flex-1 bg-slate-100 rounded-xl p-6 shadow-sm flex flex-col">
                                    <div class="flex flex-col items-center text-xl sm:text-2xl font-bold text-gray-800 mb-4 text-center gap-3">
                                        <span class="font-logo block">In-person meetings</span> 
                                        <span class="text-lg font-medium block">work too!</span>
                                    </div>
                                    <div class="flex flex-col items-center justify-center flex-grow">
                                        <img src="/assets/images/homepage/inperson-meeting.svg" alt="In-person Meeting" class="w-24 sm:w-36">
                                        <!--<p class="text-gray-600 text-center">
                                            Record and transcribe your in-person<br>
                                            meetings and discussions.
                                        </p>-->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="my-12">
                        <HowItWorks />
                    </div>

                    <!-- CTA Section -->
                    <div class="sm:my-16 py-12 bg-slate-100 rounded-2xl">
                        <div class="max-w-2xl mx-auto text-center px-6">
                            <h2 class="text-2xl font-bold text-gray-800 mb-4">Ready to Transform Your Note-Taking?</h2>
                            <!-- <p class="text-gray-600 mb-8">Join thousands of professionals who are already taking smarter, more efficient notes with Jimmy.</p> -->
                            <p class="text-gray-600 mb-8">Join us to start taking smarter, more efficient notes with Jimmy the Cricket.</p>
                            <Link 
                                v-if="canRegister" 
                                :href="route('register')" 
                                class="inline-flex items-center 
                                    px-8 sm:px-10 py-4 sm:py-4 rounded-lg text-xl font-bold 
                                    text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 
                                    hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <span>Register now</span>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </Link>
                        </div>
                    </div>

                    <!-- Video Demo Section -->
                    <!--
                    <div class="mb-16">
                        <h2 class="text-3xl font-bold text-center text-gray-900 mb-8">See it in Action</h2>
                        <div class="max-w-full w-96 sm:w-[80%] aspect-video bg-gray-100 rounded-lg overflow-hidden mx-auto">
                            <iframe 
                                class="w-full h-full"
                                src="https://www.youtube.com/embed/XXg1yVmDkKI"
                                title="Jimmy the Cricket - Product Launch"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen
                            ></iframe>
                        </div>
                    </div>
                    -->

                    <!-- Second CTA Section
                    <!-/-
                    <div class="w-full p-10 bg-gradient-to-br from-gray-50 to-gray-100 py-16">
                        <div class="max-w-4xl mx-auto px-6 text-center">
                    <div class="mb-16 py-12 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
                        <div class="max-w-2xl mx-auto text-center px-6">
                            <h2 class="text-3xl font-bold text-gray-800 mb-4">Start Your Journey to Better Notes</h2>
                            <p class="text-gray-600 mb-8 max-w-2xl mx-auto">Experience the power of AI-enhanced note-taking. No credit card required, start for free today.</p>
                            <Link 
                                v-if="canRegister" 
                                :href="route('register')" 
                                class="inline-flex items-center px-8 py-4 rounded-lg font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <span>Start Taking Better Notes</span>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                    -->

                    <!-- Browser Compatibility -->
                    <div class="relative w-full max-w-4xl px-6 mt-8 mx-auto">
                        <div v-if="isChromium" class="max-w-2xl mx-auto p-6 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg shadow-sm border border-green-100">
                            <div class="text-center">
                                <h2 class="text-2xl font-semibold text-green-800 mb-2">Available on Chromium Browsers</h2>
                                <p class="text-sm text-green-700 mb-6">Meeting recording is only available on these browsers</p>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-96 mx-auto">
                                    <div class="flex items-center justify-center sm:justify-left transition-all duration-200 hover:text-green-900">
                                        <img src="/assets/images/browser-logos/chrome-logo.svg" alt="Chrome" class="w-8 h-8 mr-3">
                                        Google Chrome
                                    </div>
                                    <div class="flex items-center justify-center sm:justify-left transition-all duration-200 hover:text-green-900">
                                        <img src="/assets/images/browser-logos/edge-logo.svg" alt="Edge" class="w-8 h-8 mr-3">
                                        Microsoft Edge
                                    </div>
                                    <div class="flex items-center justify-center sm:justify-left transition-all duration-200 hover:text-green-900">
                                        <img src="/assets/images/browser-logos/brave-logo.svg" alt="Brave" class="w-8 h-8 mr-3">
                                        Brave Browser
                                    </div>
                                    <div class="flex items-center justify-center sm:justify-left transition-all duration-200 hover:text-green-900">
                                        <img src="/assets/images/browser-logos/opera-logo.svg" alt="Opera" class="w-8 h-8 mr-3">
                                        Opera
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="max-w-2xl mx-auto p-6 bg-gradient-to-r from-amber-50 to-red-50 rounded-lg shadow-sm border border-amber-100">
                            <div class="text-center">
                                <h2 class="text-2xl font-semibold text-amber-800 mb-2">Browser Not Fully Compatible</h2>
                                <p class="text-sm text-amber-700 mb-4">Your current browser is not fully compatible with all features.</p>
                                <p class="text-sm text-amber-700">For the best experience, please use a Chromium-based browser like Google Chrome, Microsoft Edge, Brave, or Opera.</p>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            <!-- Privacy Commitment -->
            <div class="mt-16 flex justify-center">
                <div class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border-2 border-gray-200 shadow-sm">
                    <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                    <div class="flex flex-col items-center">
                        <span class="uppercase tracking-wider text-xs font-bold text-gray-500">Our Privacy Commitment</span>
                        <span class="text-xs">We Do Not Retain Audio from your Meetings</span>
                    </div>
                </div>
            </div>

            <!-- Footer Links -->
            <nav class="mt-16 flex justify-center space-x-6 text-sm text-gray-500">
                <Link :href="route('about')" class="hover:text-gray-900">About</Link>
                <Link :href="route('blog')" class="hover:text-gray-900">Blog</Link>
                <Link :href="route('about') + '#privacy-section'" class="hover:text-gray-900">Privacy Policy</Link>
                <Link :href="route('about') + '#impressum-section'" class="hover:text-gray-900">Impressum</Link>
            </nav>

            <!-- Added bottom spacing -->
            <div class="pb-16"></div>
        </div>
    </div>
</template>

<style>
.word-container {
    @apply relative h-[1.7em] min-w-[4ch] w-[300px] sm:mx-2;
}

.word {
    @apply block sm:text-left;
}

.word-underline {
    @apply absolute left-0 right-0 max-sm:mx-auto w-full border-b-[10px] opacity-80 border-green-500 transition-all duration-300 ease-in-out;
}

.word-enter-active,
.word-leave-active {
    @apply duration-300 ease-in-out;
}

.word-enter-from {
    @apply opacity-0 transform translate-y-20;
}

.word-leave-to {
    @apply opacity-0 transform -translate-y-[20px];
}

.word-enter-to,
.word-leave-from {
    @apply opacity-100 transform translate-y-0;
}

.underline-browser {
    position: relative;
    border-bottom: none;
}

.underline-browser::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 4px;
    background: #47ce5f;
    opacity: 0.8;
}

h3.text-lg {
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>